import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components/native";
import { Modal, Platform, ScrollView, TouchableOpacity } from "react-native";
import RadioButton from "@components/atoms/RadioButton";
import ArrowDownIcon from "@components/molecules/CollapsibleView/ArrowDownIcon";
import { Animated } from "react-native";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { Spacer, VDBText } from "@components/atoms";
import { getBoxShadow } from "@utils/product/productValidators";
import { SpaceType } from "@components/atoms/Spacer/types";
import { DefaultTheme } from "styled-components/native";

type DropdownWithPortalProps = {
  items: any;
  selected: string;
  onSelect: (value: string) => void;
  placeholder: string;
  screenText: string;
};

const DropdownWithPortal = ({
  items,
  selected,
  onSelect,
  placeholder,
  screenText,
}: DropdownWithPortalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, typography, t } = useScreenOptionsHooks();
  const [dropdownLayout, setDropdownLayout] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const dropdownRef = useRef(null);

  const handleDropdownPress = () => {
    if (dropdownRef.current) {
      setTimeout(() => {
        // @ts-ignore
        dropdownRef.current.measureInWindow((x, y, width, height) => {
          const normalizedX = x % window.innerWidth;
          setDropdownLayout({ x: normalizedX, y, width, height });
          setIsOpen(true);
        });
      }, 0);
    }
  };

  const rotateAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(rotateAnim, {
      toValue: isOpen ? 1 : 0,
      duration: 200,
      useNativeDriver: Platform.OS !== "web",
    }).start();
  }, [isOpen]);

  const rotateAnimDeg = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  return (
    <>
      <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
        {t(screenText)}
      </VDBText>
      <Spacer size={SpaceType.Double} />
      <TouchableOpacity ref={dropdownRef} onPress={handleDropdownPress}>
        <DropdownHeader>
          <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
            {selected || placeholder}
          </VDBText>
          <Animated.View style={{ transform: [{ rotate: rotateAnimDeg }] }}>
            <ArrowDownIcon color={theme["core-04"]} />
          </Animated.View>
        </DropdownHeader>
      </TouchableOpacity>

      <Modal visible={isOpen} transparent={true}>
        <Overlay onPress={() => setIsOpen(false)}>
          <DropdownCard
            activeOpacity={1}
            style={{
              position: "absolute",
              top: dropdownLayout.y + dropdownLayout.height + 2, // Position below the dropdown
              left: dropdownLayout.x,
              width: dropdownLayout.width, // Match the dropdown width
            }}
          >
            <ScrollView
              showsVerticalScrollIndicator={true}
              nestedScrollEnabled={true}
            >
              {items.map((label: string, index: number) => (
                <Option key={index}>
                  <RadioButton
                    label={label}
                    checked={selected.toLowerCase() === label.toLowerCase()}
                    labelColor={
                      selected.toLowerCase() === label.toLowerCase()
                        ? theme["core-05"]
                        : theme["core-06"]
                    }
                    styles={
                      selected.toLowerCase() === label.toLowerCase()
                        ? typography["Body-M"]
                        : typography["Body-R"]
                    }
                    onPress={() => {
                      onSelect(label);
                      setIsOpen(false);
                    }}
                    margin="10"
                  />
                </Option>
              ))}
            </ScrollView>
          </DropdownCard>
        </Overlay>
      </Modal>
    </>
  );
};

const DropdownHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-width: 1px;
  border-radius: 8px;
  border-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-04"] as string};
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
`;

const Overlay = styled.TouchableOpacity`
  flex: 1;
`;

const DropdownCard = styled.TouchableOpacity`
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  border-radius: 10px;
  border-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-04"] as string};
  padding-left: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  elevation: 5;
  box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
    getBoxShadow(theme.Shadows.Normal)};
  max-height: 330px;
`;

const Option = styled.View`
  margin-vertical: 1px;
`;

export default DropdownWithPortal;
